import React, { useCallback, useEffect, useMemo, useState } from 'react';

const PARTNER_OPTIONS = [
  { value: 'GETYOURGUIDE', label: 'GetYourGuide' },
  { value: 'TRAVELPAYOUTS', label: 'Travelpayouts' },
];

const WIDGET_TYPE_OPTIONS = [
  { value: 'activity_auto', label: 'Activity Widget (Automatic)' },
  { value: 'activity_manual', label: 'Activity Widget (Manual)' },
  { value: 'city', label: 'City Widget' },
  { value: 'availability', label: 'Availability Widget' },
];

const STATUS_OPTIONS = ['draft', 'active', 'archived'];

function normalizeTags(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input.map((tag) => String(tag || '').trim()).filter(Boolean);
  return [];
}

function extractAttr(html, attrName) {
  const raw = String(html || '');
  if (!raw) return '';
  const match = raw.match(new RegExp(`${attrName}="([^"]*)"`, 'i'));
  return match?.[1] || '';
}

function buildGygEmbed(attrs, innerHtml = '') {
  const attrText = attrs
    .map(([key, value]) => `${key}="${String(value || '').replace(/"/g, '&quot;')}"`)
    .join(' ');
  return `<div ${attrText}>${innerHtml}</div>`;
}

function buildGygActivitiesEmbed({
  query,
  localeCode = '{{locale}}',
  numberOfItems = 20,
  campaignId = '',
  partnerId = 'BEWYOUX',
}) {
  const safeQuery = String(query || '').trim();
  const safeLocale = String(localeCode || '{{locale}}').trim() || '{{locale}}';
  const safeNumber = Math.max(1, Math.min(50, Number.parseInt(String(numberOfItems || 20), 10) || 20));
  const safeCampaign = String(campaignId || '').trim();
  const safePartner = String(partnerId || 'BEWYOUX').trim() || 'BEWYOUX';
  return buildGygEmbed([
    ['data-gyg-href', 'https://widget.getyourguide.com/default/activities.frame'],
    ['data-gyg-locale-code', safeLocale],
    ['data-gyg-widget', 'activities'],
    ['data-gyg-number-of-items', String(safeNumber)],
    ['data-gyg-cmp', safeCampaign],
    ['data-gyg-partner-id', safePartner],
    ['data-gyg-q', safeQuery],
  ], `<span>Powered by <a target="_blank" rel="sponsored" href="https://www.getyourguide.com/s/?q=${encodeURIComponent(safeQuery)}">GetYourGuide</a></span>`);
}

function buildGygCityEmbed({
  locationId,
  query = '',
  localeCode = '{{locale}}',
  numberOfItems = 12,
  campaignId = '',
  partnerId = 'BEWYOUX',
}) {
  const safeLocationId = String(locationId || '').trim();
  const safeQuery = String(query || '').trim();
  const safeLocale = String(localeCode || '{{locale}}').trim() || '{{locale}}';
  const safeNumber = Math.max(1, Math.min(50, Number.parseInt(String(numberOfItems || 12), 10) || 12));
  const safeCampaign = String(campaignId || '').trim();
  const safePartner = String(partnerId || 'BEWYOUX').trim() || 'BEWYOUX';
  if (safeLocationId) {
    return buildGygEmbed([
      ['data-gyg-href', 'https://widget.getyourguide.com/default/city.frame'],
      ['data-gyg-location-id', safeLocationId],
      ['data-gyg-locale-code', safeLocale],
      ['data-gyg-widget', 'city'],
      ['data-gyg-cmp', safeCampaign],
      ['data-gyg-partner-id', safePartner],
    ]);
  }
  if (!safeQuery) return '';
  return buildGygEmbed([
    ['data-gyg-href', 'https://widget.getyourguide.com/default/activities.frame'],
    ['data-gyg-locale-code', safeLocale],
    ['data-gyg-widget', 'activities'],
    ['data-gyg-number-of-items', String(safeNumber)],
    ['data-gyg-cmp', safeCampaign],
    ['data-gyg-partner-id', safePartner],
    ['data-gyg-q', safeQuery],
  ], `<span>Powered by <a target="_blank" rel="sponsored" href="https://www.getyourguide.com/s/?q=${encodeURIComponent(safeQuery)}">GetYourGuide</a></span>`);
}

function buildGygAvailabilityEmbed({
  tourId,
  localeCode = '{{locale}}',
  currency = 'EUR',
  variant = 'horizontal',
  campaignId = '',
  partnerId = 'BEWYOUX',
}) {
  const safeTourId = String(tourId || '').trim();
  const safeLocale = String(localeCode || '{{locale}}').trim() || '{{locale}}';
  const safeCurrency = String(currency || 'EUR').trim().toUpperCase() || 'EUR';
  const safeVariant = String(variant || 'horizontal').trim() || 'horizontal';
  const safeCampaign = String(campaignId || '').trim();
  const safePartner = String(partnerId || 'BEWYOUX').trim() || 'BEWYOUX';
  if (!safeTourId) return '';
  return buildGygEmbed([
    ['data-gyg-href', 'https://widget.getyourguide.com/default/availability.frame'],
    ['data-gyg-tour-id', safeTourId],
    ['data-gyg-locale-code', safeLocale],
    ['data-gyg-currency', safeCurrency],
    ['data-gyg-widget', 'availability'],
    ['data-gyg-variant', safeVariant],
    ['data-gyg-cmp', safeCampaign],
    ['data-gyg-partner-id', safePartner],
  ]);
}

function extractTourIdFromText(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const slugMatch = raw.match(/-t(\d+)(?:\b|[/?#&])/i);
  if (slugMatch?.[1]) return slugMatch[1];
  const numericMatch = raw.match(/\b(\d{3,})\b/);
  return numericMatch?.[1] || '';
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeTourUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.includes('partner_id=')) return raw;
  try {
    const parsed = new URL(raw);
    parsed.searchParams.set('partner_id', 'BEWYOUX');
    return parsed.toString();
  } catch (err) {
    const sep = raw.includes('?') ? '&' : '?';
    return `${raw}${sep}partner_id=BEWYOUX`;
  }
}

function createEmptyManualTour() {
  return {
    title: '',
    url: '',
    narrative: '',
  };
}

function parseManualActivityWidgetMeta(embedHtml) {
  const raw = String(embedHtml || '');
  const match = raw.match(/<!--\s*ss-manual-activity-widget:(.*?)-->/s);
  if (!match?.[1]) return null;
  try {
    const parsed = JSON.parse(match[1]);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (err) {
    return null;
  }
}

function buildManualActivityWidgetEmbed(items = []) {
  const normalizedItems = (Array.isArray(items) ? items : [])
    .map((item, index) => ({
      index: Number.parseInt(String(item?.index || item?.rank || index + 1), 10) || index + 1,
      tourId: extractTourIdFromText(item?.tourId || item?.url || ''),
      title: String(item?.title || '').trim(),
      url: normalizeTourUrl(item?.url || ''),
      narrative: String(item?.narrative || '').trim(),
    }))
    .filter((item) => item.url && item.tourId);
  if (!normalizedItems.length) return '';
  const metaJson = JSON.stringify({ version: 1, items: normalizedItems }).replace(/-->/g, '--&gt;');
  const widgetsHtml = normalizedItems.map((item) => [
    '<div',
    ' data-gyg-href="https://widget.getyourguide.com/default/activities.frame"',
    ' data-gyg-locale-code="{{locale}}"',
    ' data-gyg-widget="activities"',
    ' data-gyg-number-of-items="1"',
    ' data-gyg-partner-id="BEWYOUX"',
    ` data-gyg-tour-ids="${escapeHtml(item.tourId)}">`,
    `<span>Powered by <a target="_blank" rel="sponsored" href="${escapeHtml(item.url)}">GetYourGuide</a></span>`,
    '</div>',
  ].join('')).join('');
  return [
    `<!-- ss-manual-activity-widget:${metaJson} -->`,
    '<div data-ss-widget="activity-manual" style="margin:18px 0 10px 0;display:grid;gap:14px;">',
    widgetsHtml,
    '</div>',
  ].join('');
}

function parseWidgetFormFromEmbed(widget = {}) {
  const embedHtml = widget.embed_html || '';
  const manualMeta = parseManualActivityWidgetMeta(embedHtml);
  return {
    title: widget.title || '',
    partner: widget.partner || 'GETYOURGUIDE',
    widgetType: widget.widget_type || '',
    embedHtml,
    tags: Array.isArray(widget.tags) ? widget.tags : [],
    status: widget.status || 'draft',
    gygQuery: extractAttr(embedHtml, 'data-gyg-q') || '',
    gygLocaleCode: extractAttr(embedHtml, 'data-gyg-locale-code') || '{{locale}}',
    gygNumberOfItems: extractAttr(embedHtml, 'data-gyg-number-of-items') || '20',
    gygCampaignId: extractAttr(embedHtml, 'data-gyg-cmp') || '',
    gygPartnerId: extractAttr(embedHtml, 'data-gyg-partner-id') || 'BEWYOUX',
    gygLocationId: extractAttr(embedHtml, 'data-gyg-location-id') || '',
    gygCityQuery: extractAttr(embedHtml, 'data-gyg-q') || '',
    gygCityNumberOfItems: extractAttr(embedHtml, 'data-gyg-number-of-items') || '12',
    gygTourId: extractAttr(embedHtml, 'data-gyg-tour-id') || '',
    gygCurrency: extractAttr(embedHtml, 'data-gyg-currency') || 'EUR',
    gygVariant: extractAttr(embedHtml, 'data-gyg-variant') || 'horizontal',
    gygActivityUrl: '',
    manualTours: Array.isArray(manualMeta?.items) && manualMeta.items.length
      ? manualMeta.items.map((item) => ({
        title: String(item?.title || '').trim(),
        url: String(item?.url || '').trim(),
        narrative: String(item?.narrative || '').trim(),
      }))
      : [createEmptyManualTour()],
  };
}

function getStructuredGygRequirements(widgetType) {
  switch (widgetType) {
    case 'activity_auto':
      return ['Query', 'Language', 'Number of Items'];
    case 'activity_manual':
      return ['At least one tour URL or tour ID'];
    case 'city':
      return ['Location ID or City Query', 'Language'];
    case 'availability':
      return ['Tour ID', 'Language', 'Currency'];
    default:
      return [];
  }
}

function getStructuredGygMissingFields(form) {
  switch (form.widgetType) {
    case 'activity_auto':
      return [
        !String(form.gygQuery || '').trim() ? 'Query' : null,
        !String(form.gygLocaleCode || '').trim() ? 'Language' : null,
        !String(form.gygNumberOfItems || '').trim() ? 'Number of Items' : null,
      ].filter(Boolean);
    case 'activity_manual': {
      const tours = Array.isArray(form.manualTours) ? form.manualTours : [];
      const hasValidTour = tours.some((tour) => String(tour?.url || '').trim() && extractTourIdFromText(tour?.tourId || tour?.url || ''));
      return hasValidTour ? [] : ['At least one tour URL or tour ID'];
    }
    case 'city':
      return [
        !String(form.gygLocationId || '').trim() && !String(form.gygCityQuery || '').trim()
          ? 'Location ID or City Query'
          : null,
        !String(form.gygLocaleCode || '').trim() ? 'Language' : null,
      ].filter(Boolean);
    case 'availability':
      return [
        !String(form.gygTourId || '').trim() ? 'Tour ID' : null,
        !String(form.gygLocaleCode || '').trim() ? 'Language' : null,
        !String(form.gygCurrency || '').trim() ? 'Currency' : null,
      ].filter(Boolean);
    default:
      return [];
  }
}

function buildStructuredGygEmbed(form) {
  if (form.partner !== 'GETYOURGUIDE') return '';
  if (form.widgetType === 'activity_auto') {
    if (getStructuredGygMissingFields(form).length) return '';
    return buildGygActivitiesEmbed({
      query: form.gygQuery,
      localeCode: form.gygLocaleCode,
      numberOfItems: form.gygNumberOfItems,
      campaignId: form.gygCampaignId,
      partnerId: form.gygPartnerId,
    });
  }
  if (form.widgetType === 'activity_manual') {
    if (getStructuredGygMissingFields(form).length) return '';
    return buildManualActivityWidgetEmbed(form.manualTours);
  }
  if (form.widgetType === 'city') {
    if (getStructuredGygMissingFields(form).length) return '';
    return buildGygCityEmbed({
      locationId: form.gygLocationId,
      query: form.gygCityQuery,
      localeCode: form.gygLocaleCode,
      numberOfItems: form.gygCityNumberOfItems,
      campaignId: form.gygCampaignId,
      partnerId: form.gygPartnerId,
    });
  }
  if (form.widgetType === 'availability') {
    if (getStructuredGygMissingFields(form).length) return '';
    return buildGygAvailabilityEmbed({
      tourId: form.gygTourId,
      localeCode: form.gygLocaleCode,
      currency: form.gygCurrency,
      variant: form.gygVariant,
      campaignId: form.gygCampaignId,
      partnerId: form.gygPartnerId,
    });
  }
  return '';
}

function TagInput({ value = [], suggestions = [], onChange }) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);

  const normalized = useMemo(() => value.map((tag) => tag.trim()).filter(Boolean), [value]);
  const uniqueSuggestions = useMemo(() => {
    const pool = new Set(suggestions.map((tag) => tag.trim()).filter(Boolean));
    normalized.forEach((tag) => pool.delete(tag));
    return Array.from(pool);
  }, [suggestions, normalized]);

  const visibleSuggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (!q) return uniqueSuggestions.slice(0, 8);
    return uniqueSuggestions.filter((tag) => tag.toLowerCase().includes(q)).slice(0, 8);
  }, [draft, uniqueSuggestions]);

  const pushTag = useCallback((tag) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    if (normalized.includes(trimmed)) return;
    onChange?.([...normalized, trimmed]);
    setDraft('');
  }, [normalized, onChange]);

  const removeTag = useCallback((tag) => {
    onChange?.(normalized.filter((item) => item !== tag));
  }, [normalized, onChange]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      if (draft.trim()) pushTag(draft);
      return;
    }
    if (event.key === 'Backspace' && !draft) {
      removeTag(normalized[normalized.length - 1]);
    }
  };

  return (
    <div className="rounded border border-gray-700 bg-gray-900 px-3 py-2">
      <div className="flex flex-wrap gap-2">
        {normalized.map((tag) => (
          <span key={tag} className="flex items-center gap-2 rounded-full bg-gray-800 px-3 py-1 text-xs text-gray-200">
            {tag}
            <button
              type="button"
              className="text-gray-400 hover:text-white"
              onClick={() => removeTag(tag)}
              aria-label={`Remove ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[140px] bg-transparent text-sm text-white outline-none"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={handleKeyDown}
          placeholder="Add tag and press Enter"
        />
      </div>
      {focused && visibleSuggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {visibleSuggestions.map((tag) => (
            <button
              key={tag}
              type="button"
              className="rounded-full border border-gray-700 px-3 py-1 text-xs text-gray-300 hover:border-gray-500 hover:text-white"
              onClick={() => pushTag(tag)}
            >
              + {tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TagFilter({ value = [], suggestions = [], onChange }) {
  const handleToggle = (tag) => {
    if (value.includes(tag)) {
      onChange(value.filter((item) => item !== tag));
    } else {
      onChange([...value, tag]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {suggestions.map((tag) => (
        <button
          key={tag}
          type="button"
          className={`rounded-full px-3 py-1 text-xs ${
            value.includes(tag)
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
          onClick={() => handleToggle(tag)}
        >
          {tag}
        </button>
      ))}
    </div>
  );
}

export default function WidgetsManager({ openWidgetId = null, openRequestKey = 0 }) {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState([]);
  const [form, setForm] = useState({
    title: '',
    partner: 'GETYOURGUIDE',
    widgetType: 'activity_auto',
    embedHtml: '',
    tags: [],
    status: 'draft',
    gygQuery: '',
    gygLocaleCode: '{{locale}}',
    gygNumberOfItems: '20',
    gygCampaignId: '',
    gygPartnerId: 'BEWYOUX',
    gygLocationId: '',
    gygCityQuery: '',
    gygCityNumberOfItems: '12',
    gygTourId: '',
    gygCurrency: 'EUR',
    gygVariant: 'horizontal',
    gygActivityUrl: '',
    manualTours: [createEmptyManualTour()],
  });

  const resetForm = useCallback(() => {
    setEditId(null);
    setValidationAttempted(false);
    setForm(parseWidgetFormFromEmbed({
      title: '',
      partner: 'GETYOURGUIDE',
      widget_type: 'activity_auto',
      embed_html: '',
      tags: [],
      status: 'draft',
    }));
  }, []);

  const loadWidgets = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/creative/widgets', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Failed to load widgets.');
      }
      const data = await res.json();
      setWidgets(Array.isArray(data.widgets) ? data.widgets : []);
    } catch (err) {
      setWidgets([]);
      setError(err.message || 'Failed to load widgets.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWidgets();
  }, [loadWidgets]);

  const filteredWidgets = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    const tagsFilter = normalizeTags(tagFilter);
    return widgets.filter((widget) => {
      if (statusFilter !== 'all' && widget.status !== statusFilter) return false;
      if (partnerFilter !== 'all' && widget.partner !== partnerFilter) return false;
      if (q) {
        const haystack = [
          widget.title,
          widget.partner,
          widget.widget_type,
          ...(widget.tags || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (tagsFilter.length) {
        const widgetTags = Array.isArray(widget.tags) ? widget.tags : [];
        if (!tagsFilter.some((tag) => widgetTags.includes(tag))) return false;
      }
      return true;
    });
  }, [widgets, search, statusFilter, partnerFilter, tagFilter]);

  const tagSuggestions = useMemo(() => {
    const pool = new Set();
    widgets.forEach((widget) => {
      if (Array.isArray(widget.tags)) {
        widget.tags.forEach((tag) => pool.add(tag));
      }
    });
    return Array.from(pool);
  }, [widgets]);

  const structuredRequirements = useMemo(
    () => getStructuredGygRequirements(form.widgetType),
    [form.widgetType]
  );

  const structuredMissingFields = useMemo(
    () => getStructuredGygMissingFields(form),
    [form]
  );

  const structuredEmbedHtml = useMemo(
    () => buildStructuredGygEmbed(form),
    [form]
  );

  const usesStructuredGygControls = form.partner === 'GETYOURGUIDE'
    && ['activity_auto', 'activity_manual', 'city', 'availability'].includes(form.widgetType);

  const handleEdit = useCallback((widget) => {
    setEditId(widget.id);
    setValidationAttempted(false);
    setForm(parseWidgetFormFromEmbed(widget));
  }, []);

  useEffect(() => {
    if (!openWidgetId) return;
    const widget = widgets.find((item) => String(item.id) === String(openWidgetId));
    if (!widget) return;
    handleEdit(widget);
  }, [widgets, openWidgetId, openRequestKey, handleEdit]);

  useEffect(() => {
    if (!structuredEmbedHtml) return;
    const nextEmbedHtml = structuredEmbedHtml;
    setForm((prev) => (prev.embedHtml === nextEmbedHtml ? prev : { ...prev, embedHtml: nextEmbedHtml }));
  }, [structuredEmbedHtml]);

  const loadStructuredFieldsFromEmbed = useCallback(() => {
    setForm((prev) => {
      const parsed = parseWidgetFormFromEmbed({
        title: prev.title,
        partner: prev.partner,
        widget_type: prev.widgetType,
        embed_html: prev.embedHtml,
        tags: prev.tags,
        status: prev.status,
      });
      return { ...prev, ...parsed, gygActivityUrl: prev.gygActivityUrl };
    });
  }, []);

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setValidationAttempted(true);
    setSaving(true);
    setError('');
    try {
      let embedHtml = structuredEmbedHtml || String(form.embedHtml || '').trim();
      if (!embedHtml && usesStructuredGygControls && structuredMissingFields.length) {
        throw new Error(`Complete the required fields for this widget type: ${structuredMissingFields.join(', ')}`);
      }
      const payload = {
        title: String(form.title || '').trim(),
        partner: String(form.partner || '').trim(),
        widgetType: String(form.widgetType || '').trim(),
        embedHtml,
        tags: normalizeTags(form.tags),
        status: String(form.status || '').trim() || 'draft',
      };
      const url = editId ? `/api/creative/widgets/${editId}` : '/api/creative/widgets';
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save widget.');
      const saved = data.widget;
      if (!saved) throw new Error('Invalid widget response.');
      setWidgets((prev) => {
        if (editId) {
          return prev.map((item) => (item.id === saved.id ? saved : item));
        }
        return [saved, ...prev];
      });
      setEditId(saved.id);
      setValidationAttempted(false);
      setForm(parseWidgetFormFromEmbed(saved));
    } catch (err) {
      setError(err.message || 'Failed to save widget.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (widget) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (!window.confirm(`Archive widget "${widget.title}"?`)) return;
    try {
      const res = await fetch(`/api/creative/widgets/${widget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Failed to archive widget.');
      }
      setWidgets((prev) =>
        prev.map((item) =>
          item.id === widget.id ? { ...item, status: 'archived' } : item
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to archive widget.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold">Widgets</h2>
        <button
          className="px-3 py-1 rounded bg-gray-700 text-white"
          onClick={loadWidgets}
          disabled={loading}
        >
          Refresh
        </button>
        <button
          className="px-3 py-1 rounded bg-indigo-600 text-white"
          onClick={resetForm}
        >
          New Widget
        </button>
      </div>

      {error && <div className="text-red-400">{error}</div>}

      <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
        <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-semibold">{editId ? 'Edit Widget' : 'Create Widget'}</h3>
            <button
              className="px-3 py-1 rounded bg-indigo-600 text-white text-sm disabled:opacity-50"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
          <div className="grid gap-3">
            <label className="text-sm text-gray-300">
              Title
              <input
                className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Widget title"
              />
            </label>
            <label className="text-sm text-gray-300">
              Partner
              <select
                className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={form.partner}
                onChange={(e) => setForm((prev) => ({ ...prev, partner: e.target.value }))}
              >
                {PARTNER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-300">
              Widget Type
              <select
                className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={form.widgetType}
                onChange={(e) => {
                  const widgetType = e.target.value;
                  setValidationAttempted(false);
                  setForm((prev) => ({ ...prev, widgetType }));
                }}
              >
                <option value="">Select type</option>
                {WIDGET_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-300">
              Embed Code
              <textarea
                className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white h-40 font-mono text-xs"
                value={form.embedHtml}
                onChange={(e) => setForm((prev) => ({ ...prev, embedHtml: e.target.value }))}
                placeholder="<script>...</script>"
              />
            </label>
            {usesStructuredGygControls && (
              <div className="rounded border border-gray-700 bg-gray-950/40 p-3 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm text-gray-200 font-medium">
                    {form.widgetType === 'activity_auto' && 'GetYourGuide Automatic Activity Controls'}
                    {form.widgetType === 'activity_manual' && 'GetYourGuide Manual Activity Controls'}
                    {form.widgetType === 'city' && 'GetYourGuide City Widget Controls'}
                    {form.widgetType === 'availability' && 'GetYourGuide Availability Widget Controls'}
                  </div>
                  <button
                    type="button"
                    className="px-2 py-1 rounded bg-gray-800 text-xs text-white"
                    onClick={loadStructuredFieldsFromEmbed}
                  >
                    Load Fields From Embed
                  </button>
                </div>
                <div className="text-xs text-gray-400">
                  Structured fields update the embed code automatically. You can still edit the raw embed manually if needed.
                </div>
                <div className="rounded border border-amber-800/60 bg-amber-950/20 px-3 py-2 text-xs text-amber-200">
                  Required before structured save: {structuredRequirements.length ? structuredRequirements.join(', ') : 'None'}
                </div>
                {(validationAttempted && usesStructuredGygControls && structuredMissingFields.length && !String(form.embedHtml || '').trim()) && (
                  <div className="rounded border border-red-800/60 bg-red-950/20 px-3 py-2 text-xs text-red-200">
                    Missing required fields: {structuredMissingFields.join(', ')}
                  </div>
                )}
                {form.widgetType === 'activity_auto' && (
                  <div className="grid gap-2 md:grid-cols-2">
                    <label className="text-xs text-gray-300">
                      Query <span className="text-amber-300">*</span>
                      <input
                        className={`mt-1 w-full rounded border px-2 py-2 text-white ${
                          !String(form.gygQuery || '').trim()
                            ? 'border-amber-700 bg-gray-800'
                            : 'border-gray-700 bg-gray-800'
                        }`}
                        value={form.gygQuery}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygQuery: e.target.value }))}
                        placeholder="gold coast water sports"
                      />
                    </label>
                    <label className="text-xs text-gray-300">
                      Language <span className="text-amber-300">*</span>
                      <input
                        className={`mt-1 w-full rounded border px-2 py-2 text-white ${
                          !String(form.gygLocaleCode || '').trim()
                            ? 'border-amber-700 bg-gray-800'
                            : 'border-gray-700 bg-gray-800'
                        }`}
                        value={form.gygLocaleCode}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygLocaleCode: e.target.value }))}
                        placeholder="{{locale}} or en-US"
                      />
                    </label>
                    <label className="text-xs text-gray-300">
                      Number of Items <span className="text-amber-300">*</span>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        className={`mt-1 w-full rounded border px-2 py-2 text-white ${
                          !String(form.gygNumberOfItems || '').trim()
                            ? 'border-amber-700 bg-gray-800'
                            : 'border-gray-700 bg-gray-800'
                        }`}
                        value={form.gygNumberOfItems}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygNumberOfItems: e.target.value }))}
                      />
                    </label>
                    <label className="text-xs text-gray-300">
                      Campaign ID
                      <input
                        className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-2 py-2 text-white"
                        value={form.gygCampaignId}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygCampaignId: e.target.value }))}
                        placeholder="SCV-44-gold-coast-water-sports"
                      />
                    </label>
                    <label className="text-xs text-gray-300 md:col-span-2">
                      Partner ID
                      <input
                        className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-2 py-2 text-white"
                        value={form.gygPartnerId}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygPartnerId: e.target.value }))}
                        placeholder="BEWYOUX"
                      />
                    </label>
                  </div>
                )}
                {form.widgetType === 'activity_manual' && (
                  <div className="space-y-3">
                    <div className="text-xs text-gray-400">
                      Add the exact tours you want to present. Each item generates a single GetYourGuide tour widget from the partner-linked URL or tour ID. Title and narrative are optional metadata for your own editing flow.
                    </div>
                    {(Array.isArray(form.manualTours) ? form.manualTours : []).map((tour, index) => {
                      const urlMissing = !String(tour?.url || '').trim();
                      return (
                        <div key={`manual-tour-${index}`} className="rounded border border-gray-700 bg-gray-900/70 p-3 space-y-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-300">
                              Top Pick {index + 1}
                            </div>
                            <button
                              type="button"
                              className="rounded bg-gray-800 px-2 py-1 text-xs text-white"
                              onClick={() =>
                                setForm((prev) => {
                                  const nextTours = (Array.isArray(prev.manualTours) ? prev.manualTours : [])
                                    .filter((_, itemIndex) => itemIndex !== index);
                                  return {
                                    ...prev,
                                    manualTours: nextTours.length ? nextTours : [createEmptyManualTour()],
                                  };
                                })
                              }
                              disabled={(Array.isArray(form.manualTours) ? form.manualTours : []).length <= 1}
                            >
                              Remove
                            </button>
                          </div>
                          <label className="block text-xs text-gray-300">
                            Title
                            <input
                              className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-2 text-white"
                              value={tour.title}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  manualTours: (Array.isArray(prev.manualTours) ? prev.manualTours : []).map((item, itemIndex) => (
                                    itemIndex === index ? { ...item, title: e.target.value } : item
                                  )),
                                }))
                              }
                              placeholder="Moreton Island: Dolphin & Tangalooma Wrecks Snorkel Cruise"
                            />
                          </label>
                          <label className="block text-xs text-gray-300">
                            Tour URL <span className="text-amber-300">*</span>
                            <input
                              className={`mt-1 w-full rounded border px-2 py-2 text-white ${
                                urlMissing ? 'border-amber-700 bg-gray-800' : 'border-gray-700 bg-gray-800'
                              }`}
                              value={tour.url}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  manualTours: (Array.isArray(prev.manualTours) ? prev.manualTours : []).map((item, itemIndex) => (
                                    itemIndex === index ? { ...item, url: e.target.value } : item
                                  )),
                                }))
                              }
                              placeholder="https://www.getyourguide.com/activity/-t74468?partner_id=BEWYOUX"
                            />
                          </label>
                          <label className="block text-xs text-gray-300">
                            Narrative
                            <textarea
                              className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-2 text-white h-24"
                              value={tour.narrative}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  manualTours: (Array.isArray(prev.manualTours) ? prev.manualTours : []).map((item, itemIndex) => (
                                    itemIndex === index ? { ...item, narrative: e.target.value } : item
                                  )),
                                }))
                              }
                              placeholder="Embark on an adventurous full-day trip..."
                            />
                          </label>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      className="rounded bg-indigo-700 px-3 py-2 text-xs text-white"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          manualTours: [...(Array.isArray(prev.manualTours) ? prev.manualTours : []), createEmptyManualTour()],
                        }))
                      }
                    >
                      Add Tour
                    </button>
                  </div>
                )}
                {form.widgetType === 'city' && (
                  <div className="grid gap-2 md:grid-cols-2">
                    <label className="text-xs text-gray-300">
                      Location ID
                      <input
                        className={`mt-1 w-full rounded border px-2 py-2 text-white ${
                          !String(form.gygLocationId || '').trim() && !String(form.gygCityQuery || '').trim()
                            ? 'border-amber-700 bg-gray-800'
                            : 'border-gray-700 bg-gray-800'
                        }`}
                        value={form.gygLocationId}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygLocationId: e.target.value }))}
                        placeholder="200"
                      />
                    </label>
                    <label className="text-xs text-gray-300">
                      City Query
                      <input
                        className={`mt-1 w-full rounded border px-2 py-2 text-white ${
                          !String(form.gygLocationId || '').trim() && !String(form.gygCityQuery || '').trim()
                            ? 'border-amber-700 bg-gray-800'
                            : 'border-gray-700 bg-gray-800'
                        }`}
                        value={form.gygCityQuery}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygCityQuery: e.target.value }))}
                        placeholder="Hoi An"
                      />
                    </label>
                    <label className="text-xs text-gray-300">
                      Language <span className="text-amber-300">*</span>
                      <input
                        className={`mt-1 w-full rounded border px-2 py-2 text-white ${
                          !String(form.gygLocaleCode || '').trim()
                            ? 'border-amber-700 bg-gray-800'
                            : 'border-gray-700 bg-gray-800'
                        }`}
                        value={form.gygLocaleCode}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygLocaleCode: e.target.value }))}
                        placeholder="{{locale}} or en-US"
                      />
                    </label>
                    <label className="text-xs text-gray-300">
                      Number of Items
                      <input
                        type="number"
                        min="1"
                        max="50"
                        className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-2 py-2 text-white"
                        value={form.gygCityNumberOfItems}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygCityNumberOfItems: e.target.value }))}
                      />
                    </label>
                    <label className="text-xs text-gray-300">
                      Campaign ID
                      <input
                        className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-2 py-2 text-white"
                        value={form.gygCampaignId}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygCampaignId: e.target.value }))}
                        placeholder="SCV-44-gold-coast-city"
                      />
                    </label>
                    <label className="text-xs text-gray-300">
                      Partner ID
                      <input
                        className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-2 py-2 text-white"
                        value={form.gygPartnerId}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygPartnerId: e.target.value }))}
                        placeholder="BEWYOUX"
                      />
                    </label>
                  </div>
                )}
                {form.widgetType === 'availability' && (
                  <div className="grid gap-2 md:grid-cols-2">
                    <label className="text-xs text-gray-300 md:col-span-2">
                      Activity URL
                      <input
                        className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-2 py-2 text-white"
                        value={form.gygActivityUrl}
                        onChange={(e) => {
                          const nextUrl = e.target.value;
                          const extractedTourId = extractTourIdFromText(nextUrl);
                          setForm((prev) => ({
                            ...prev,
                            gygActivityUrl: nextUrl,
                            gygTourId: extractedTourId || prev.gygTourId,
                          }));
                        }}
                        placeholder="https://www.getyourguide.com/activity/-t108793"
                      />
                      <div className="mt-1 text-[11px] text-gray-500">
                        Optional. Paste a GetYourGuide activity URL and the Tour ID will be extracted if possible.
                      </div>
                    </label>
                    <label className="text-xs text-gray-300">
                      Tour ID <span className="text-amber-300">*</span>
                      <input
                        className={`mt-1 w-full rounded border px-2 py-2 text-white ${
                          !String(form.gygTourId || '').trim()
                            ? 'border-amber-700 bg-gray-800'
                            : 'border-gray-700 bg-gray-800'
                        }`}
                        value={form.gygTourId}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygTourId: e.target.value }))}
                        placeholder="108793"
                      />
                    </label>
                    <label className="text-xs text-gray-300">
                      Language <span className="text-amber-300">*</span>
                      <input
                        className={`mt-1 w-full rounded border px-2 py-2 text-white ${
                          !String(form.gygLocaleCode || '').trim()
                            ? 'border-amber-700 bg-gray-800'
                            : 'border-gray-700 bg-gray-800'
                        }`}
                        value={form.gygLocaleCode}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygLocaleCode: e.target.value }))}
                        placeholder="{{locale}} or en-US"
                      />
                    </label>
                    <label className="text-xs text-gray-300">
                      Currency <span className="text-amber-300">*</span>
                      <input
                        className={`mt-1 w-full rounded border px-2 py-2 text-white ${
                          !String(form.gygCurrency || '').trim()
                            ? 'border-amber-700 bg-gray-800'
                            : 'border-gray-700 bg-gray-800'
                        }`}
                        value={form.gygCurrency}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygCurrency: e.target.value.toUpperCase() }))}
                        placeholder="EUR"
                      />
                    </label>
                    <label className="text-xs text-gray-300">
                      Layout Variant
                      <select
                        className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-2 py-2 text-white"
                        value={form.gygVariant}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygVariant: e.target.value }))}
                      >
                        <option value="horizontal">Horizontal</option>
                        <option value="vertical">Vertical</option>
                      </select>
                    </label>
                    <label className="text-xs text-gray-300">
                      Campaign ID
                      <input
                        className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-2 py-2 text-white"
                        value={form.gygCampaignId}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygCampaignId: e.target.value }))}
                        placeholder="museum-featured-ticket"
                      />
                    </label>
                    <label className="text-xs text-gray-300 md:col-span-2">
                      Partner ID
                      <input
                        className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-2 py-2 text-white"
                        value={form.gygPartnerId}
                        onChange={(e) => setForm((prev) => ({ ...prev, gygPartnerId: e.target.value }))}
                        placeholder="BEWYOUX"
                      />
                    </label>
                  </div>
                )}
                <div className="text-[11px] text-gray-500">
                  Use <code>{'{{locale}}'}</code> to follow the plaque language automatically, or enter a fixed code like <code>en-US</code>, <code>de-DE</code>, <code>fr-FR</code>.
                </div>
              </div>
            )}
            <label className="text-sm text-gray-300">
              Tags
              <div className="mt-1">
                <TagInput
                  value={form.tags}
                  suggestions={tagSuggestions}
                  onChange={(tags) => setForm((prev) => ({ ...prev, tags }))}
                />
              </div>
            </label>
            <label className="text-sm text-gray-300">
              Status
              <select
                className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
          <div className="mb-3 grid gap-3">
            <input
              className="w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search widgets..."
            />
            <div className="grid gap-2 sm:grid-cols-2">
              <select
                className="rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <select
                className="rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
              >
                <option value="all">All Partners</option>
                {PARTNER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {tagSuggestions.length > 0 && (
              <TagFilter
                value={tagFilter}
                suggestions={tagSuggestions}
                onChange={setTagFilter}
              />
            )}
          </div>

          {loading && <div className="text-gray-300">Loading widgets...</div>}
          {!loading && filteredWidgets.length === 0 && (
            <div className="text-gray-400">No widgets found.</div>
          )}
          <div className="space-y-3">
            {filteredWidgets.map((widget) => (
              <div
                key={widget.id}
                className="rounded border border-gray-800 bg-gray-950 p-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold">{widget.title}</div>
                    <div className="text-xs text-gray-400">
                      {widget.partner}{widget.widget_type ? ` · ${widget.widget_type}` : ''}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-gray-800 px-2 py-1 text-xs text-gray-200">
                      {widget.status}
                    </span>
                    <button
                      className="rounded bg-indigo-600 px-2 py-1 text-xs text-white"
                      onClick={() => handleEdit(widget)}
                    >
                      Edit
                    </button>
                    {widget.status !== 'archived' && (
                      <button
                        className="rounded bg-gray-700 px-2 py-1 text-xs text-white"
                        onClick={() => handleArchive(widget)}
                      >
                        Archive
                      </button>
                    )}
                  </div>
                </div>
                {Array.isArray(widget.tags) && widget.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {widget.tags.map((tag) => (
                      <span key={tag} className="rounded bg-gray-800 px-2 py-1 text-xs text-gray-200">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {widget.embed_html && (
                  <details className="mt-2 text-xs text-gray-400">
                    <summary className="cursor-pointer">Embed code</summary>
                    <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-gray-900 p-2 text-gray-300">
                      {widget.embed_html}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
